// 폭발 플립북 시트(절차 생성): 8×8 = 64프레임을 한 장의 텍스처로 미리 그린다. 외부 이미지를 받지 않는다.
// 나중에 Blender 시뮬 헤드리스 렌더(PRD 3A.3 "플립북" 층)로 바꿀 수 있도록 시트 규약을 고정한다(vfx-library/README.md "폭발 플립북 시트 규약"):
//   - 격자 8열 × 8행, 프레임 번호 f = 0..63, 열 = f % 8, 행 = floor(f / 8). 행 0이 이미지 맨 위, 프레임은 왼쪽 위부터 오른쪽으로(읽는 순서).
//   - 프레임 시간 u = f / 63: 폭발 시작(0) → 연기가 흩어져 사라짐(1). 모듈은 수명 전체에 걸쳐 0 → 63을 같은 속도로 재생한다.
//   - 채널: R = 불(발광 세기 0..1), G = 연기(밀도 0..1), B = max(R, G)(참고용 마스크), A = 255.
//     프레임 가장자리 6%는 비운다(선형 보간이 이웃 프레임으로 새지 않게).
//   - 프레임 정사각형, 중심 = 폭발 중심, 폭발은 프레임 지름 안에 들어온다. 8비트 RGBA, 색 공간 없음(데이터 텍스처).
// 지금 값은 CPU 절차 생성이다(불꽃 덩어리 + 난류 가장자리 + 날아가는 불똥 + 올라가며 퍼지는 연기). Blender 렌더로 바꿔도 같은 규약이면 코드는 그대로다.
import { fbm } from './noise';

export const BLAST_GRID = 8;
export const BLAST_FRAMES = BLAST_GRID * BLAST_GRID;
/** 프레임 한 장의 한 변(px). 시트 한 변 = 8 × 96 = 768 */
export const BLAST_FRAME_PX = 96;
const SPARKS = 16;

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const easeOut = (u: number): number => 1 - (1 - u) * (1 - u) * (1 - u);
const hash1 = (i: number, k: number): number => {
  let h = Math.imul(i + 1, 374761393) ^ Math.imul(k + 7, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
};

/** 진행 0..1 → 프레임 번호 0..63 (셰이더도 같은 식) */
export function blastFrame(age01: number): number {
  const a = age01 < 0 ? 0 : age01 > 0.9999 ? 0.9999 : age01;
  return Math.floor(a * BLAST_FRAMES);
}

/** 프레임 f의 칸 위치(열, 행; 행 0 = 이미지 맨 위) */
export function blastCell(f: number): { col: number; row: number } {
  return { col: f % BLAST_GRID, row: Math.floor(f / BLAST_GRID) };
}

function drawFrame(data: Uint8Array, sheetPx: number, f: number, F: number): void {
  const u = f / (BLAST_FRAMES - 1);
  const { col, row } = blastCell(f);
  const x0 = col * F;
  // 데이터 행 0 = v 0(아래). 이미지 행 row는 위에서 센다
  const yBottom = (BLAST_GRID - 1 - row) * F;

  // 불: 덩어리가 빠르게 커졌다가 식어서 사라진다
  const R = 0.16 + 0.5 * easeOut(Math.min(1, u / 0.42));
  const I = 1 - smooth(0.12, 0.5, u);
  // 연기: 천천히 커지며 위로 번지고 뒤로 가며 흩어진다
  const Rs = 0.22 + 0.5 * easeOut(Math.min(1, u / 0.9));
  const S = smooth(0.06, 0.28, u) * (1 - smooth(0.6, 1.0, u));

  for (let ly = 0; ly < F; ly++) {
    const y = ((ly + 0.5) / F) * 2 - 1;
    for (let lx = 0; lx < F; lx++) {
      const x = ((lx + 0.5) / F) * 2 - 1;
      const r = Math.sqrt(x * x + y * y);
      if (r >= 1) continue;
      const edge = 1 - smooth(0.86, 0.94, r);
      let fire = 0, smoke = 0;
      if (I > 0.001) {
        const n1 = fbm(x * 2.4 + 3.7, y * 2.4 - u * 1.6 + 9.1, 11, 3);
        const rr = r + (n1 - 0.5) * 0.9 * R;
        const mask = 1 - smooth(R * 0.45, R, rr);
        const core = 1 - smooth(0, R * 0.7, r);
        fire = Math.pow(mask, 1.1) * (0.4 + 0.6 * core) * I;
      }
      if (S > 0.001) {
        const n2 = fbm(x * 1.7 + 20, y * 1.7 - u * 0.7 + 4, 23, 4);
        const rs = r + (n2 - 0.5) * 0.8 * Rs - y * 0.12 * u;
        const mask = 1 - smooth(Rs * 0.2, Rs, rs);
        smoke = mask * S * (0.45 + 0.55 * n2);
      }
      if (fire === 0 && smoke === 0) continue;
      const o = ((yBottom + ly) * sheetPx + x0 + lx) * 4;
      data[o] = Math.min(255, Math.round(fire * edge * 255));
      data[o + 1] = Math.min(255, Math.round(smoke * (1 - smooth(0.6, 0.92, r)) * 255));
    }
  }

  // 불똥: 가운데서 사방으로 날아가며 중력으로 처진다(불 채널에 작은 점)
  if (u < 0.6) {
    for (let i = 0; i < SPARKS; i++) {
      const a = hash1(i, 1) * Math.PI * 2;
      const sp = 0.45 + 0.5 * hash1(i, 2);
      const life = 0.3 + 0.3 * hash1(i, 3);
      if (u > life) continue;
      const k = easeOut(u / life);
      const sx = Math.cos(a) * sp * (0.12 + 0.78 * k);
      const sy = Math.sin(a) * sp * (0.12 + 0.78 * k) - 0.22 * u * u;
      const fade = 1 - u / life;
      const rad = 0.025 + 0.02 * fade;
      const px = ((sx + 1) / 2) * F, py = ((sy + 1) / 2) * F;
      const pr = Math.ceil(rad * F) + 1;
      for (let dy = -pr; dy <= pr; dy++) {
        for (let dx = -pr; dx <= pr; dx++) {
          const ix = Math.floor(px) + dx, iy = Math.floor(py) + dy;
          if (ix < 0 || iy < 0 || ix >= F || iy >= F) continue;
          const cx = (ix + 0.5) / F * 2 - 1, cy = (iy + 0.5) / F * 2 - 1;
          if (cx * cx + cy * cy >= 0.88 * 0.88) continue;
          const d = Math.hypot(cx - sx, cy - sy);
          const v = Math.max(0, 1 - d / rad) * (0.7 + 0.3 * fade);
          if (v <= 0) continue;
          const o = ((yBottom + iy) * sheetPx + x0 + ix) * 4;
          const cur = data[o];
          const nv = Math.min(255, Math.round(v * 255));
          if (nv > cur) data[o] = nv;
        }
      }
    }
  }

  // B = max(R, G), A = 255
  for (let ly = 0; ly < F; ly++) {
    for (let lx = 0; lx < F; lx++) {
      const o = ((yBottom + ly) * sheetPx + x0 + lx) * 4;
      data[o + 2] = Math.max(data[o], data[o + 1]);
      data[o + 3] = 255;
    }
  }
}

/** 시트 RGBA8 데이터(한 변 8 × framePx). 데이터 행 0이 아래다(WebGL 텍스처 규약) */
export function buildBlastSheetData(framePx = BLAST_FRAME_PX): Uint8Array {
  const px = framePx * BLAST_GRID;
  const data = new Uint8Array(px * px * 4);
  for (let f = 0; f < BLAST_FRAMES; f++) drawFrame(data, px, f, framePx);
  return data;
}

let cache: Uint8Array | null = null;
/** 프로세스에서 한 번만 만든다(모듈 인스턴스마다 다시 그리지 않는다). 읽기 전용으로 다룬다 */
export function blastSheetData(): Uint8Array {
  if (cache === null) cache = buildBlastSheetData();
  return cache;
}
